import { AlertTriangle, X } from "lucide-react";
import { DsButton } from "../../../design-system";

const formatAssigneeLabel = (label) => {
  const text = String(label || "").trim();
  if (!text) return "Awaiting assignment";
  if (text === text.toUpperCase() && /[A-Z]/.test(text)) {
    return text.charAt(0) + text.slice(1).toLowerCase();
  }
  return text;
};

export default function PolicyDeleteImpactModal({
  open,
  mode = "blocked",
  impact,
  policyName,
  deleting = false,
  onClose,
  onConfirmDelete,
}) {
  if (!open || !impact) return null;

  const isBlocked = mode === "blocked";
  const typeLabel = impact.policyTypeLabel || "Policy";
  const pendingRows = impact.byAssignee || [];
  const pendingTotal = impact.pendingTotal ?? 0;

  return (
    <div className="fixed inset-0 z-[10006] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-[min(100%,420px)] flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
        <div
          className={`flex items-start justify-between gap-3 border-b px-5 py-4 ${
            isBlocked
              ? "border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)]"
              : "border-[hsl(var(--ds-danger)/0.35)] bg-[hsl(var(--ds-danger)/0.08)]"
          }`}
        >
          <div className="flex items-start gap-2.5">
            <AlertTriangle
              className={`mt-0.5 h-5 w-5 shrink-0 ${
                isBlocked ? "text-[hsl(var(--ds-warning-fg))]" : "text-[hsl(var(--ds-danger))]"
              }`}
            />
            <div>
              <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">
                {isBlocked ? "Delete blocked" : "Delete policy?"}
              </h3>
              {policyName && (
                <p className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
                  {policyName} · {typeLabel}
                </p>
              )}
            </div>
          </div>
          <DsButton type="button" variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X size={18} />
          </DsButton>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4 text-sm text-[hsl(var(--ds-fg))]">
          {isBlocked ? (
            <>
              <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)] px-4 py-3">
                <span className="font-medium text-[hsl(var(--ds-warning-fg))]">Pending requests</span>
                <span className="rounded-full bg-[hsl(var(--ds-warning)/0.25)] px-2.5 py-0.5 text-sm font-bold tabular-nums text-[hsl(var(--ds-warning-fg))]">
                  {pendingTotal}
                </span>
              </div>

              {pendingRows.length > 0 && (
                <ul className="space-y-2">
                  {pendingRows.map((row) => (
                    <li
                      key={row.assignee}
                      className="flex items-center justify-between rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2.5"
                    >
                      <span className="font-medium text-[hsl(var(--ds-fg))]">
                        {formatAssigneeLabel(row.assignee)}
                      </span>
                      <span className="rounded-full bg-[hsl(var(--ds-surface))] px-2 py-0.5 text-xs font-semibold tabular-nums text-[hsl(var(--ds-fg))]">
                        {row.count}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <p className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2.5 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                Approve or reject each item from{" "}
                <strong>Employee → Dashboard → Action Insights</strong>. Or use{" "}
                <strong>Deactivate</strong> to stop new cases.
              </p>
            </>
          ) : (
            <p className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2.5 text-[hsl(var(--ds-fg))]">
              No pending requests linked. This permanently removes the policy.
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-5 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={onClose} disabled={deleting}>
            {isBlocked ? "Close" : "Cancel"}
          </DsButton>
          {!isBlocked && (
            <DsButton
              type="button"
              variant="danger"
              size="sm"
              onClick={onConfirmDelete}
              disabled={deleting}
              loading={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </DsButton>
          )}
        </div>
      </div>
    </div>
  );
}
